import { useCallback, useEffect, useRef, useState } from 'react';

import {
  clearInFlightTagRequests,
  createTag,
  deleteTags,
  fetchTags,
  mergeTags as mergeTagsApi,
  renameTag,
} from './api';
import { extractTagErrorMessage, sortTagsByName } from './helpers';
import type { MetadataDialogKind, ResourceTagType, TagOption } from './types';
import { useMetadataFieldConfig } from './use-metadata-field-config';

interface UseTagCatalogParams {
  kind: MetadataDialogKind;
  enabled?: boolean;
  selectedTagsFromModel?: TagOption[];
}

// Tags rarely change outside this hook, so the catalog is cached for the page load and every
// mutation writes through it — reopening a dropdown reuses the list instead of refetching.
// fetchTags already dedupes concurrent requests, so this layer only caches results.
const cachedTagsByType = new Map<ResourceTagType, TagOption[]>();
// Bumped on reset so a request started under the previous account cannot write to the new one.
let cacheGeneration = 0;

export function resetTagCatalogCache() {
  cachedTagsByType.clear();
  clearInFlightTagRequests();
  cacheGeneration += 1;
}

function mergeTags(currentTags: TagOption[], nextTags: TagOption[]) {
  if (nextTags.length === 0) return sortTagsByName(currentTags);

  const mergedById = new Map(currentTags.map((tag) => [tag.id, tag]));

  nextTags.forEach((tag) => {
    mergedById.set(tag.id, tag);
  });

  return sortTagsByName(Array.from(mergedById.values()));
}

export function useTagCatalog({
  kind,
  enabled = true,
  selectedTagsFromModel = [],
}: UseTagCatalogParams) {
  const fieldConfig = useMetadataFieldConfig(kind);
  const { title, label, tagType } = fieldConfig;
  const [tags, setTags] = useState<TagOption[]>(() => cachedTagsByType.get(tagType) ?? []);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [hasLoadedTags, setHasLoadedTags] = useState(() => cachedTagsByType.has(tagType));
  const [loadErrorMessage, setLoadErrorMessage] = useState('');
  const tagsRef = useRef<TagOption[]>(cachedTagsByType.get(tagType) ?? []);
  const hasLoadedRef = useRef(cachedTagsByType.has(tagType));
  const selectedTagsFromModelRef = useRef(selectedTagsFromModel);

  // Writes started before a reset belong to the previous account, so they never reach the cache.
  const commitTags = useCallback(
    (nextTags: TagOption[], generation: number) => {
      if (generation !== cacheGeneration) return;

      setTags(nextTags);
      tagsRef.current = nextTags;
      cachedTagsByType.set(tagType, nextTags);
    },
    [tagType],
  );

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  useEffect(() => {
    selectedTagsFromModelRef.current = selectedTagsFromModel;
  }, [selectedTagsFromModel]);

  useEffect(() => {
    if (selectedTagsFromModel.length === 0) return;

    setTags((currentTags) => mergeTags(currentTags, selectedTagsFromModel));
  }, [selectedTagsFromModel]);

  const ensureTagsLoaded = useCallback(async () => {
    if (!enabled || hasLoadedRef.current) return;

    const generation = cacheGeneration;
    const cachedTags = cachedTagsByType.get(tagType);
    try {
      setLoadErrorMessage('');
      if (!cachedTags) setIsLoadingTags(true);
      const fetchedTags = cachedTags ?? (await fetchTags(tagType));
      if (generation !== cacheGeneration) return;

      commitTags(mergeTags(fetchedTags, selectedTagsFromModelRef.current), generation);
      hasLoadedRef.current = true;
      setHasLoadedTags(true);
    } catch (error) {
      setLoadErrorMessage(`Failed to load ${title}. Please retry.`);
      console.error(`Failed to load ${tagType.replace(/_/g, ' ')} tags:`, error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [commitTags, enabled, tagType, title]);

  const create = useCallback(
    async (name: string) => {
      const generation = cacheGeneration;
      try {
        const currentTags = tagsRef.current;
        const created = await createTag(tagType, name);
        const nextTags = sortTagsByName([...currentTags, created]);
        commitTags(nextTags, generation);
        return { created, nextTags };
      } catch (error) {
        const createFallback = 'Failed to create tag. Please retry.';
        throw new Error(extractTagErrorMessage(error, createFallback));
      }
    },
    [commitTags, tagType],
  );

  const rename = useCallback(
    async (id: string, name: string) => {
      const generation = cacheGeneration;
      try {
        const currentTags = tagsRef.current;
        const updated = await renameTag(tagType, id, name);
        const nextTags = sortTagsByName(
          currentTags.map((tag) => (tag.id === updated.id ? updated : tag)),
        );
        commitTags(nextTags, generation);
        return { updated, nextTags };
      } catch (error) {
        const renameFallback = `Failed to rename ${label.toLowerCase()}.`;
        throw new Error(extractTagErrorMessage(error, renameFallback));
      }
    },
    [commitTags, label, tagType],
  );

  const mergeMany = useCallback(
    async (sourceIds: string[], targetId: string) => {
      const generation = cacheGeneration;
      try {
        const currentTags = tagsRef.current;
        const mergedTarget = await mergeTagsApi(tagType, sourceIds, targetId);
        const removeIds = new Set(sourceIds);
        const nextTags = sortTagsByName(
          currentTags
            .filter((tag) => !removeIds.has(tag.id))
            .map((tag) => (tag.id === mergedTarget.id ? mergedTarget : tag)),
        );
        commitTags(nextTags, generation);
        return { mergedTarget, nextTags };
      } catch (error) {
        const mergeFallback = `Failed to replace ${label.toLowerCase()}.`;
        throw new Error(extractTagErrorMessage(error, mergeFallback));
      }
    },
    [commitTags, label, tagType],
  );

  const removeMany = useCallback(
    async (ids: string[]) => {
      const generation = cacheGeneration;
      try {
        const currentTags = tagsRef.current;
        await deleteTags(tagType, ids);
        const removeIds = new Set(ids);
        const nextTags = currentTags.filter((tag) => !removeIds.has(tag.id));
        commitTags(nextTags, generation);
        return { nextTags };
      } catch (error) {
        const deleteFallback = `Failed to delete ${label.toLowerCase()}.`;
        throw new Error(extractTagErrorMessage(error, deleteFallback));
      }
    },
    [commitTags, label, tagType],
  );
  return {
    tags,
    hasLoadedTags,
    isLoadingTags,
    loadErrorMessage,
    ensureTagsLoaded,
    create,
    rename,
    mergeMany,
    removeMany,
  };
}
